<script>
import i18n from "@/i18n";
import {mapActions, mapState} from "pinia";
import {profileStore} from "@/store/profileStore";
import {bankIdStore} from "@/store/bankIdStore";
import {authStore} from "@/store/authStore";
import {takeOverStore} from "@/store/takeOverStore";
import BaseAvatar from "@/components/BaseAvatar/BaseAvatar.vue";
import ProfileElectedInfo from "@/views/profile/ProfileElectedInfo.vue";
import {shareStore} from "@/store/shareStore";

export default {
  name: "ProfileGeneralInfo",
  components: {
    BaseAvatar,
    ProfileElectedInfo
  },
  props: {
    profileInfo: {
      type: Object
    },
    profileSettings: {
      type: Object
    },
    isOwnProfile: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      profileViewFullMode: true
    }
  },
  computed: {
    ...mapState(profileStore, [
      'memberProfile',
      'politicianProfile'
    ]),
    ...mapState(authStore, [
      'userLoggedIn',
      'isManagerProfile'
    ]),
    bgImg() {
      const src = `/api/viewheroimg?modelId=${this.profileInfo.id}&modelType=USER`;
      return {
        'background-image': `url("${src}")`,
      };
    },
    profileAvatarHash() {
      if (this.memberProfile) {
        return this.memberProfile.user.largeImgHash;
      } else if (this.politicianProfile) {
        return this.politicianProfile.user.largeImgHash;
      }
      return this.profileInfo.avatarUrl;
    },
    profileBirthdayPrivate() {
      return this.profileSettings['BIRTHDAY_PRIVACY'] === 'PRIVATE';
    },
    profileGenderPrivate() {
      return this.profileSettings['GENDER_PRIVACY'] === 'PRIVATE';
    },
    profileEmailPrivate() {
      return this.profileSettings['EMAIL_PRIVACY'] === 'PRIVATE';
    },
    profileWebsitePrivate() {
      return this.profileSettings['WEB_SITE_PRIVACY'] === 'PRIVATE';
    },
    profileAboutPrivate() {
      return this.profileSettings['ABOUT_PRIVACY'] === 'PRIVATE';
    },
    profileName() {
      if (this.memberProfile) {
        return `${this.memberProfile.firstName} ${this.memberProfile.lastName}`
      } else if (this.politicianProfile) {
        return `${this.politicianProfile.firstName} ${this.politicianProfile.lastName}`
      }
      return `${this.profileInfo.firstName} ${this.profileInfo.lastName}`
    },
    profileGender() {
      if (this.memberProfile) {
        return i18n.global.t(`profile['${this.memberProfile.gender ? this.memberProfile.gender : "NONE"}']`);
      } else if (this.politicianProfile) {
        return i18n.global.t(`profile['${this.politicianProfile.gender ? this.politicianProfile.gender : "NONE"}']`);
      }

      return i18n.global.t(`profile['${this.profileInfo.gender ? this.profileInfo.gender : "NONE"}']`);

    },
    profileEmail() {
      return this.memberProfile && this.memberProfile.emails.length > 0
          ? this.memberProfile.emails.filter(email=>email.primary)[0].email
          : '';
    },
    profileWebsites() {
      let webSites = [];

      if (this.memberProfile ) {
        webSites = this.memberProfile.webSite ?  this.memberProfile.webSite.split('\n') : [];
      } else if (this.politicianProfile) {
        webSites = this.politicianProfile.webSite ? this.politicianProfile.webSite.split('\n') : [];
      }

      webSites.length > 0 ? webSites.forEach((item)=> {
        return !item.includes("http") ? 'https://' + item : false;
      }) : [];

      return webSites;
    },
    aboutTextEmpty() {
      return this.isOwnProfile
          ? `${i18n.global.t('profile.youHaventEnteredAboutMe')}`
          : `${this.profileName} ${i18n.global.t('profile.hasntEnteredAboutMe')}`;
    },
    profilePoliticianInfo() {
      return this.profileInfo.politicianData[0];
    },
    hasMemberProfile() {
      return this.memberProfile !== null;
    },
    hasPoliticianProfile() {
      return this.politicianProfile !== null;
    },
    politicianIsNotVerified() {
      return this.hasPoliticianProfile && !this.hasMemberProfile;
    },
    politicianIsVerified() {
      return this.hasPoliticianProfile && this.hasMemberProfile;
    },
    takeOverEnabled() {
      return this.politicianIsNotVerified && !this.isOwnProfile;
    }
  },
  methods: {
    ...mapActions(bankIdStore, [
      'checkBankIdAuthSession'
    ]),
    ...mapActions(authStore, [
      'authGetUserData',
      'toggleLoginModalState'
    ]),
    ...mapActions(takeOverStore, [
        'initTakeOver'
    ]),
    ...mapActions(shareStore, [
      'toggleShareModal',
    ]),
    toggleProfileViewMode() {
      this.profileViewFullMode = !this.profileViewFullMode;
    },
    toggleStartTakeOverAccount() {
      if (this.politicianIsVerified) return;

      this.initTakeOver();
    },
    goToEditProfile() {
      if (this.isManagerProfile) return;
      this.$router.push({ name: 'EditProfileView', params: { id: this.profileInfo.id }});
    },
    shareProfile() {
      this.toggleShareModal();
    }
  },
}
</script>

<template>
<div class="profile-general-info-wrap">
  <div v-if="profileViewFullMode" class="profile-bg-block">
    <div class="profile-bg-img" :style="bgImg"></div>
    <div v-show="politicianIsVerified" class="based-upon-official-data-img"></div>
  </div>
  <div class="profile-main-data" :class="{'profile-main-data--minimized': !profileViewFullMode}">
    <div class="profile-info">
        <base-avatar v-show="profileViewFullMode"
                     size="125"
                     class="profile-avatar"
                     :img-hash="profileAvatarHash"
        />
        <base-avatar v-show="!profileViewFullMode"
                     size="75"
                     class="profile-avatar"
                     :img-hash="profileAvatarHash"
        />
      <profile-elected-info v-if="profileInfo.politicianData.length > 0"
                            :elected-info="profilePoliticianInfo"
      />
      <div class="profile-data">
        <div class="d-flex align-item-center mb-10">
          <span class="profile-title">{{profileName}}</span>
          <template v-if="takeOverEnabled">
            <div  class="user-verified-control"
                  @click="toggleStartTakeOverAccount()"
                  v-tooltip.top="$t('takeOver.isThisYou')"
            >
              <font-awesome-icon :icon="['fas', 'check']" />
            </div>
          </template>
          <template v-else>
            <div class="user-verified-control user-verified-control--verified"
                 v-tooltip.top="$t('takeOver.verifiedProfile')"
            >
              <font-awesome-icon :icon="['fas', 'check']" />
            </div>
          </template>
        </div>
        <div v-if="profileViewFullMode" class="d-flex">
          <div class="profile-data-items-list">
            <div class="profile-data-item profile-data-item--birth">
              <i class="profile-data-item__icon"></i>
              <template v-if="!profileBirthdayPrivate || isOwnProfile">
                <span class="profile-data-item__text">
                  {{ profileInfo.birthday }}
                </span>
              </template>
              <template v-else>
                <span class="private-label">{{$t('privacy.private')}}</span>
              </template>
            </div>
            <div class="profile-data-item profile-data-item--gender">
              <i class="profile-data-item__icon"></i>
              <template v-if="!profileGenderPrivate || isOwnProfile">
                <span class="profile-data-item__text">
                  {{ profileGender }}
                </span>
              </template>
              <template v-else>
                <span class="private-label">{{$t('privacy.private')}}</span>
              </template>
            </div>
          </div>
          <div class="profile-data-items-list">
            <div class="profile-data-item profile-data-item--email">
              <i class="profile-data-item__icon"></i>
              <template v-if="!profileEmailPrivate || isOwnProfile">
                <a :href="`mailto:${profileEmail}`" class="profile-data-item__text">
                  {{ profileEmail }}
                </a>
              </template>
              <template v-else>
                <span class="private-label">{{$t('privacy.private')}}</span>
              </template>
            </div>
            <template v-if="!profileWebsitePrivate || isOwnProfile">
              <div v-for="(website, index) in profileWebsites"
                   :key="website+index"
                   class="profile-data-item profile-data-item--website">
                <i class="profile-data-item__icon"></i>
                <a :href="website" target="_blank">{{website}}</a>
              </div>
            </template>
            <template v-else>
              <div class="profile-data-item profile-data-item--website">
                <i class="profile-data-item__icon"></i>
                <span class="private-label">{{$t('privacy.private')}}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-if="profileViewFullMode" class="profile-about">
        <span class="profile-about__title">{{$t('profile.aboutMe')}}</span>
          <template v-if="!profileAboutPrivate || isOwnProfile">
            <template v-if="profileInfo.about">
              <div class="profile-about__text">
                {{ profileInfo.about }}
              </div>
            </template>
            <template v-else>
              <div class="profile-about__no-data">
                {{aboutTextEmpty}}
              </div>
            </template>
          </template>
          <template v-else>
            <div>
              <span class="private-label">{{$t('privacy.private')}}</span>
            </div>
          </template>
      </div>
    </div>
    <div class="profile-controls">
      <div class="profile-view-mode-switcher" @click="toggleProfileViewMode()">
        <span class="profile-view-mode-switcher__text">
          <template v-if="profileViewFullMode">{{$t('profile.minimizeProfile')}}</template>
          <template v-else>{{$t('profile.maximizeProfile')}}</template>
        </span>
        <font-awesome-icon v-if="profileViewFullMode" :icon="['fas', 'chevron-up']" class="profile-view-mode-switcher__icon"/>
        <font-awesome-icon v-else :icon="['fas', 'chevron-down']" class="profile-view-mode-switcher__icon"/>
      </div>
      <template v-if="isOwnProfile">
        <button type="button" class="edit-profile-btn" @click="goToEditProfile()"
                :class="{'edit-profile-btn--disabled': isManagerProfile}"
                v-tooltip.top="{ value: $t('header.editProfileDisabled'), class: 'edit-profile-disabled-tooltip', disabled: !isManagerProfile }"
        >
          <font-awesome-icon :icon="['fas', 'pen']" class="mr-10"/>
          {{$t('profile.editProfile')}}
        </button>
      </template>
      <div class="share-control" @click="shareProfile">
        <font-awesome-icon :icon="['fas', 'share-nodes']"
                           class="share-control__icon"
        />
        <span class="share-control__label">{{$t('share.share')}}</span>
      </div>
    </div>
  </div>
</div>
</template>

<style lang="scss" scoped>
.profile-general-info-wrap {
  width: 970px;
  margin-bottom: 10px;

  .profile-bg-block {
    position: relative;

    .profile-bg-img {
      width: 100%;
      height: 225px;
      background: #ccc;
      background-size: 100%;
      border-radius: 24px;
    }
    .based-upon-official-data-img {
      position: absolute;
      top: 10px;
      right: 35px;
      width: 150px;
      height: 150px;
      background: url("~@/assets/img/profile/swed-based-upon-official-data.png"), no-repeat, center;
      background-size: 100%;
    }
  }
  .user-verified-control {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    width: 24px;
    background: #bbbbbb;
    border-radius: 24px;
    color: #fff;
    cursor: pointer;
    &--verified {
      background: #7835a6;
      cursor: default;
    }
  }

  .profile-main-data {
    display: flex;
    justify-content: space-between;
    min-height: 250px;
    padding: 0 25px 20px 20px;
    position: relative;
    background: #fff;

    .profile-view-mode-switcher {
      position: absolute;
      top: -13px;
      right: 12px;
      width: 150px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background-color: #a163cd;
      cursor: pointer;
      &__text {
        margin-right: 5px;
        font-size: 14px;
        font-weight: 600;
        color: #FFFFFF;
      }
      &__icon {
        color: #FFFFFF;
      }
    }

    .profile-info {
      display: flex;

      .profile-avatar {
        position: absolute;
        left: 35px;
        top: -35px;
        margin-right: 25px;
      }

      .profile-elected-info {
        position: absolute;
        top: 85px;
      }

      .profile-data {
        position: absolute;
        top: 5px;
        left: 175px;
        padding: 10px;
        .profile-title {
          display: block;
          margin: 0 10px 0 5px;
          font-size: 20px;
          font-weight: 600;
          color: #000000;
        }

        .profile-data-items-list {
          min-width: 135px;
        }

        .profile-data-item {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          &__icon {
            display: block;
            width: 23px;
            height: 20px;
            margin-right: 5px;
          }
          &__text {

          }

          &--birth {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon-birthday.svg") no-repeat center;
            }
          }
          &--gender {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon-gender.svg") no-repeat center;
            }
          }
          &--email {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon-message.svg") no-repeat center;
            }
          }
          &--website {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon-website.svg") no-repeat center;
            }
          }
        }
      }

      .profile-about {
        position: relative;
        margin: 130px 0 0 35px;
        width: 550px;
        text-align: center;

        &__title {
          display: inline-block;
          width: 100%;
          margin-bottom: 10px;
          color: #000;
          text-align: left;
        }
        &__text {
          color: #4f4f4f;
          text-align: left;
        }
        &__no-data {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 70px;
          color: #bbb;
        }
      }

      .private-label {
        color: #cccccc;
      }

    }
    .profile-controls {
      padding-top: 35px;

      .edit-profile-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 35px;
        margin-left: auto;
        padding: 0 13px;
        font-weight: 600;
        border-radius: 2px;
        color: #fff;
        background: #18bafe;
        border: none;
        cursor: pointer;
        &:hover {
          background: #289de7;
        }
        &--disabled {
          cursor: default;
          background: #ccc;
          &:hover {
            background: #ccc;
          }
        }
      }
    }

    .share-control {
      display: flex;
      align-items: center;
      height: 42px;
      padding: 0 7px;
      color: #7835a6;
      border-radius: 4px;
      background: 0 0;
      border: none;
      cursor: pointer;
      &__icon {
        margin-right: 5px;
      }
      &__label {
        font-size: 16px;
        font-weight: 600;
      }
    }

    &--minimized {
      height: 105px;
      min-height: auto;
      align-items: center;
      padding-bottom: 0;
      background-image: linear-gradient(to bottom, #a163cd, #c096de);
      border-radius: 8px;

      .profile-info {
        .profile-avatar {
          position: relative;
          top: auto;
          left: auto;
        }

        .profile-data {
          left: 95px;
          top: 15px;
          .profile-title {
            font-size: 16px;
            color: #fff;
          }
        }

        .profile-elected-info {
          left: 105px;
          top: 50px
        }

      }

      .profile-controls {
        .profile-view-mode-switcher {
          position: relative;
          top: auto;
          right: auto;
          background: #fff;
          &__text {
            color: #7835a6;
          }
          &__icon {
            color: #7835a6;
          }
        }

        .share-control {
          color: #fff;
        }
      }
    }
  }

}

</style>